import React, { useEffect, useRef, useState } from 'react'
import Button from 'components/button'
import Input from 'components/input'
import Select from 'components/select'
import Spinner from 'components/spinner'
import Tooltip from 'components/tooltip'
import { Delete, Download, Plus, Warning } from 'components/icons'
import api from 'helpers/api'
import { DEFAULT_HEADER_BACKGROUND_COLOUR, DEFAULT_HEADER_TEXT_COLOUR } from 'helpers/colour'
import { validateSourceUrl } from 'helpers/utils'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { useThemedColours } from 'hooks/useTheme'
import { IHeadingStyle, IProject } from 'types'
import ColourField from 'components/colour-field'
import HeadingStyleRow from 'views/settings/components/formatting/components/headingStyleRow'
import HubPreview from 'views/settings/components/formatting/components/hubPreview'
import { SaveButtons } from 'components/save-buttons'
import {
  SETTINGS_CANVAS,
  ConfirmDialog,
  Confirmation,
  FieldRow,
  InlineRow,
  SettingsCard,
  TextLink,
  useFocusWithin
} from 'views/settings/components/settingsCard'

const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD'
const DEFAULT_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/** Fields the page saves on the hub itself. */
type StylingFields = Partial<
  Pick<
    IProject,
    | 'primaryColour'
    | 'fontColour'
    | 'defaultBanner'
    | 'wordTemplate'
    | 'wordTableStyle'
    | 'defaultDateFormat'
    | 'defaultDatetimeFormat'
    | 'defaultHeaderBackgroundColor'
    | 'defaultHeaderTextColor'
    | 'processTitleFontSize'
    | 'processTitleBold'
    | 'processTitleItalic'
    | 'processTitleUnderline'
    | 'processTitleColour'
    | 'processTitleAlignment'
  >
>

// The preview's columns: the one in focus between the edges of its neighbours.
const PREVIEW_COLUMNS = ['64px', '136px', '64px']
const PREVIEW_ROWS = 3

/**
 * A sliver of a table in the hub's colours for new columns: a header between the edges of its
 * neighbours, over a few rows, all fading out away from the column in the middle. Square-cornered, as
 * the grid is. The Tables card's preview, above the header colours.
 */
const HeaderPreview: React.FC<{ background: string; text: string }> = ({ background, text }) => {
  const themed = useThemedColours()
  const header = themed.header(background, text)
  // Fading out to each side, and downwards: the two masks overlap to leave the middle column's header.
  const fade = [
    'linear-gradient(to right, transparent, #000 24%, #000 76%, transparent)',
    'linear-gradient(to bottom, #000 50%, transparent 110%)'
  ].join(', ')
  return (
    <div
      aria-hidden
      data-testid="column-header-preview"
      className="grid border-l border-t border-divider text-[12px]"
      style={{
        gridTemplateColumns: PREVIEW_COLUMNS.join(' '),
        maskImage: fade,
        WebkitMaskImage: fade,
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in'
      }}
    >
      {PREVIEW_COLUMNS.map((_, column) => (
        <div
          key={`header-${column}`}
          className="flex h-7 items-center border-b border-r border-divider px-2 font-medium"
          style={header}
        >
          {column === 1 && 'Column'}
        </div>
      ))}
      {Array.from({ length: PREVIEW_ROWS * PREVIEW_COLUMNS.length }, (_, cell) => (
        <div key={`cell-${cell}`} className="flex h-6 items-center border-b border-r border-divider bg-surface px-2">
          <span className={`h-1.5 rounded-full bg-divider ${cell % 3 === 1 ? 'w-16' : 'w-8'}`} />
        </div>
      ))}
    </div>
  )
}

/** A name to type exactly, as a monospace badge, in the manner of a docs site: a style, a tag, a format. */
const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="whitespace-nowrap rounded-md border border-divider bg-surface-sunken px-1.5 py-px font-mono text-[12px] text-body">
    {children}
  </code>
)

// Morta's example Word template, which exports use until a hub has its own.
const DEFAULT_TEMPLATE = '/assets/docs/Morta-Export-Template.docx'

// The styles an upload is checked for, in the order the page names them. The bullet list style can
// also be "List Bullet1"; Table Grid isn't checked, but exported tables use it.
const TEMPLATE_STYLES = ['Heading 1', 'Heading 2', 'Heading 3', 'Heading 4', 'Hyperlink', 'List Bullet']

/**
 * The styles a template upload was turned down for lacking, read from the API's message: "Template
 * document is missing the following styles: Heading 2, Hyperlink", or, once those are there, "...is
 * missing a bullet list style. Expected one of: List Bullet, List Bullet1.". Null for any other failure.
 */
const missingStylesIn = (error: unknown): string[] | null => {
  const message = error instanceof Error ? error.message : ''
  if (/missing a bullet list style/.test(message)) return ['List Bullet']
  const match = /missing the following styles: (.+)$/.exec(message)
  if (!match) return null
  // In the order the page lists them, rather than the API's, which has none.
  const rank = (style: string) => {
    const index = TEMPLATE_STYLES.indexOf(style)
    return index === -1 ? TEMPLATE_STYLES.length : index
  }
  return match[1]
    .split(',')
    .map((style) => style.trim())
    .sort((a, b) => rank(a) - rank(b))
}

/**
 * Why a template was turned down, and how to fix it, in the manner of shadcn/ui's destructive alert.
 * It stays under the template until a template is accepted, as the steps take a trip to Word.
 */
const MissingStylesAlert: React.FC<{ styles: string[] }> = ({ styles }) => (
  <div role="alert" className="flex gap-3 rounded-lg border border-red/30 bg-red/5 px-4 py-3 text-[13px]">
    <Warning className="mt-0.5 text-red" />
    <div className="flex flex-col gap-1">
      <div className="font-medium text-red">
        The template is missing {styles.length === 1 ? 'the style' : 'the styles'} {styles.join(', ')}
      </div>
      <div className="text-body">
        Word only saves a style once it&apos;s been used. For each missing style:
        <ol className="m-0 mt-1 list-decimal pl-5">
          <li>Type some text in that style</li>
          <li>Delete the text, and save the document</li>
        </ol>
        Then replace the template with it again.
      </div>
    </div>
  </div>
)

const Formatting: React.FC = () => {
  const { project, updateProject } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const [savingField, setSavingField] = useState<boolean>(false)
  const [uploading, setUploading] = useState<'banner' | 'template' | null>(null)
  const [addedHeading, setAddedHeading] = useState<string | null>(null)
  const [missingStyles, setMissingStyles] = useState<string[] | null>(null)

  // Colours being picked and not yet saved, which the previews follow.
  const [hubColourDraft, setHubColourDraft] = useState<string | null>(null)
  const [textColourDraft, setTextColourDraft] = useState<string | null>(null)
  const [headerBackgroundDraft, setHeaderBackgroundDraft] = useState<string | null>(null)
  const [headerTextDraft, setHeaderTextDraft] = useState<string | null>(null)

  const [editDate, setEditDate] = useState<{ editing: boolean; text: string }>({
    editing: false,
    text: project.defaultDateFormat
  })
  const [editDatetime, setEditDatetime] = useState<{ editing: boolean; text: string }>({
    editing: false,
    text: project.defaultDatetimeFormat
  })
  const dateInput = useRef<HTMLInputElement>(null)
  const datetimeInput = useRef<HTMLInputElement>(null)
  const [dateFocused, dateFocusProps, leaveDate] = useFocusWithin()
  const [datetimeFocused, datetimeFocusProps, leaveDatetime] = useFocusWithin()

  const bannerFile = useRef<HTMLInputElement>(null)
  const templateFile = useRef<HTMLInputElement>(null)
  const [bannerSrc, setBannerSrc] = useState<string>()
  const [template, setTemplate] = useState<{ src: string; name?: string }>()

  // Follow the saved formats while they aren't being edited, so the fields show a change made elsewhere.
  useEffect(() => {
    if (!editDate.editing) setEditDate({ editing: false, text: project.defaultDateFormat })
  }, [project.defaultDateFormat])
  useEffect(() => {
    if (!editDatetime.editing) setEditDatetime({ editing: false, text: project.defaultDatetimeFormat })
  }, [project.defaultDatetimeFormat])
  // Another hub's settings: drop anything typed, or a dialog left open, for the last one.
  useEffect(() => {
    setEditDate({ editing: false, text: project.defaultDateFormat })
    setEditDatetime({ editing: false, text: project.defaultDatetimeFormat })
    setConfirmation(null)
    setMissingStyles(null)
    setHubColourDraft(null)
    setTextColourDraft(null)
    setHeaderBackgroundDraft(null)
    setHeaderTextDraft(null)
  }, [project.publicId])

  // The banner and the template are stored privately; sign them to show or download.
  useEffect(() => {
    setBannerSrc(undefined)
    if (!project.defaultBanner) return
    api
      .signUrl(project.defaultBanner)
      .then((response) => setBannerSrc(response.data.url))
      .catch(displayErrorMessage)
  }, [project.defaultBanner])
  useEffect(() => {
    setTemplate(undefined)
    if (!project.wordTemplate) return
    api
      .signUrl(project.wordTemplate)
      .then((response) => setTemplate({ src: response.data.url, name: response.data.filename }))
      .catch(displayErrorMessage)
  }, [project.wordTemplate])

  /** Saves fields on the hub. Quiet saves are those the page shows straight away, like a heading's sample. */
  const saveFields = async (fields: StylingFields, { quiet = false } = {}): Promise<boolean> => {
    setSavingField(true)
    try {
      await api.updateProject({ publicId: project.publicId, ...fields })
    } catch (error) {
      setSavingField(false)
      displayErrorMessage(error)
      return false
    }
    setSavingField(false)
    // Onto the hub as it is now, not as it was when the save began: another may have landed since.
    updateProject((current) => ({ ...current, ...fields }))
    if (!quiet) setSnackbarMessage({ status: 'success' })
    return true
  }

  const saveDateFormat = async (kind: 'date' | 'datetime', text: string) => {
    const saved = await saveFields(kind === 'date' ? { defaultDateFormat: text } : { defaultDatetimeFormat: text })
    if (!saved) return
    if (kind === 'date') {
      setEditDate({ editing: false, text })
      leaveDate()
    } else {
      setEditDatetime({ editing: false, text })
      leaveDatetime()
    }
  }

  const uploadBanner = async (file: File) => {
    setUploading('banner')
    try {
      const response = await api.uploadFile(file, [{ resource: 'project', publicId: project.publicId }])
      await saveFields({ defaultBanner: response.data.url })
    } catch (error) {
      // The API says why the image was turned down (#11118).
      displayErrorMessage(error)
    }
    setUploading(null)
  }

  const uploadTemplate = async (file: File) => {
    setUploading('template')
    let failure: unknown = null
    try {
      const response = await api.uploadTemplateFile(project.publicId, file)
      const { wordTemplate, wordTableStyle, wordTableStyles } = response.data
      updateProject((current) => ({ ...current, wordTemplate, wordTableStyle, wordTableStyles }))
      setSnackbarMessage({ status: 'success' })
    } catch (error) {
      failure = error
    }
    setUploading(null)
    // Missing styles stay on the page, with how to add them; anything else is a passing message.
    const missing = failure ? missingStylesIn(failure) : null
    setMissingStyles(missing)
    if (failure && !missing) displayErrorMessage(failure)
  }

  const removeTemplate = async () => {
    if (await saveFields({ wordTemplate: null })) {
      updateProject((current) => ({ ...current, wordTemplate: null, wordTableStyle: null, wordTableStyles: null }))
    }
  }

  // Heading changes show in the sample straight away, and go back if they don't save.
  // Each heading's changes reach the server one at a time, in the order they're made, so a quick run
  // of them can't land out of order and leave an older one saved. Every request carries the whole
  // style as it is on the page by then, changes before it included. The page shows each change
  // straight away. When one fails, the saved style goes back only if no newer change is queued behind
  // it: a newer one carries the whole style and saves it, and the page already shows it.
  const headingQueue = useRef<Record<string, Promise<unknown>>>({})
  const savedStyle = useRef<Record<string, unknown>>({})
  const changeCount = useRef<Record<string, number>>({})

  const saveInOrder = async <T,>(
    key: string,
    change: { next: T; saved: T; show: (value: T) => void; request: () => Promise<unknown> }
  ): Promise<boolean> => {
    if (!(key in savedStyle.current)) savedStyle.current[key] = change.saved
    const count = (changeCount.current[key] ?? 0) + 1
    changeCount.current[key] = count
    change.show(change.next)
    const queued = (headingQueue.current[key] ?? Promise.resolve()).then(change.request, change.request)
    headingQueue.current[key] = queued.catch(() => undefined)
    let failure: unknown = null
    try {
      await queued
    } catch (error) {
      failure = error
    }
    if (!failure) {
      savedStyle.current[key] = change.next
      return true
    }
    displayErrorMessage(failure)
    if (changeCount.current[key] === count) change.show(savedStyle.current[key] as T)
    return false
  }

  const updateDocumentNameStyle = (style: IHeadingStyle): Promise<boolean> => {
    const fields: StylingFields = {
      processTitleFontSize: style.fontSize,
      processTitleBold: style.bold,
      processTitleItalic: style.italic,
      processTitleUnderline: style.underline,
      processTitleColour: style.colour,
      processTitleAlignment: style.alignment || 'left'
    }
    return saveInOrder(`${project.publicId}:document-name`, {
      next: fields,
      saved: {
        processTitleFontSize: project.processTitleFontSize,
        processTitleBold: project.processTitleBold,
        processTitleItalic: project.processTitleItalic,
        processTitleUnderline: project.processTitleUnderline,
        processTitleColour: project.processTitleColour,
        processTitleAlignment: project.processTitleAlignment
      },
      show: (shown) => updateProject((current) => ({ ...current, ...shown })),
      request: () => api.updateProject({ publicId: project.publicId, ...fields })
    })
  }

  /** A hub with one heading style swapped for another, by its publicId. */
  const withHeadingStyle = (next: IHeadingStyle) => (current: IProject) => ({
    ...current,
    headingStyles: current.headingStyles.map((s) => (s.publicId === next.publicId ? next : s))
  })

  const updateHeadingStyle = (style: IHeadingStyle): Promise<boolean> =>
    saveInOrder(style.publicId || '', {
      next: style,
      saved: project.headingStyles.find((s) => s.publicId === style.publicId) || style,
      show: (shown) => updateProject(withHeadingStyle(shown)),
      request: () => api.updateHeadingStyle(project.publicId, style)
    })

  const addHeadingLevel = async () => {
    let styles: IHeadingStyle[]
    try {
      styles = (await api.addHeadingStyle(project.publicId)).data.headingStyles
    } catch (error) {
      displayErrorMessage(error)
      return
    }
    // The new level opens, ready to style.
    setAddedHeading(styles[styles.length - 1]?.publicId ?? null)
    updateProject((current) => ({ ...current, headingStyles: styles }))
  }

  const removeLastHeadingLevel = async () => {
    let styles: IHeadingStyle[]
    try {
      styles = (await api.removeLastHeadingStyle(project.publicId)).data.headingStyles
    } catch (error) {
      displayErrorMessage(error)
      return
    }
    updateProject((current) => ({ ...current, headingStyles: styles }))
  }

  // Gives every column in the hub the default header colours, or every date and date and time column
  // its default format, after asking: each replaces what the columns have now.
  const applyHeaderColours = () =>
    setConfirmation({
      title: 'Apply header colours to all columns?',
      message: 'Every column header in this hub takes these colours, replacing its own.',
      confirmLabel: 'Apply to All Columns',
      onConfirm: () => runApply(() => api.applyDefaultColumnColours(project.publicId))
    })

  const applyDataFormats = () =>
    setConfirmation({
      title: 'Apply these formats to all date columns?',
      message: `Every date column in this hub takes ${project.defaultDateFormat}, and every date and time column ${project.defaultDatetimeFormat}, replacing their own.`,
      confirmLabel: 'Apply to All Columns',
      onConfirm: () =>
        runApply(() =>
          api
            .applyDefaultDateFormat(project.publicId, 'date')
            .then(() => api.applyDefaultDateFormat(project.publicId, 'datetime'))
        )
    })

  const runApply = async (request: () => Promise<unknown>) => {
    try {
      await request()
    } catch (error) {
      displayErrorMessage(error)
      return
    }
    setSnackbarMessage({ status: 'success', message: 'Applied to existing columns' })
  }

  const headingCount = project.headingStyles.length

  return (
    <div className={`w-full min-h-full overflow-auto ${SETTINGS_CANVAS}`}>
      <div className="flex flex-col gap-4 max-w-[720px] mx-auto">
        <SettingsCard title="Hub theme" description="How this hub shows in the sidebar.">
          {/* Above the colours it shows, following them as they're picked. */}
          <div className="mx-4 flex justify-center pt-4 pb-3">
            <HubPreview
              name={project.name}
              colour={hubColourDraft ?? project.primaryColour}
              textColour={textColourDraft ?? project.fontColour}
            />
          </div>
          <InlineRow
            label="Hub icon background"
            description="Shown in the sidebar, so you always know at a glance where you are in Morta."
          >
            <ColourField
              label="Hub icon background"
              value={project.primaryColour}
              onDraft={setHubColourDraft}
              onSave={(colour) => saveFields({ primaryColour: colour })}
            />
          </InlineRow>
          <InlineRow label="Hub icon text colour" description="For text on the hub colour, like the badge's initials.">
            <ColourField
              label="Hub icon text colour"
              value={project.fontColour}
              onDraft={setTextColourDraft}
              onSave={(colour) => saveFields({ fontColour: colour })}
            />
          </InlineRow>
        </SettingsCard>

        <SettingsCard title="Document banner" description="Shown at the top of documents that don't have their own.">
          <div className="mx-4 py-3 flex flex-col gap-3 text-[13px]">
            {project.defaultBanner ? (
              <>
                <div className="flex aspect-[5/1] w-full items-center justify-center overflow-hidden rounded-md border border-divider bg-surface-sunken">
                  {bannerSrc ? (
                    <img
                      className="h-full w-full object-cover"
                      src={validateSourceUrl(bannerSrc)}
                      alt={`${project.name} default banner`}
                    />
                  ) : (
                    <Spinner />
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    internalType="outline"
                    isLoading={uploading === 'banner'}
                    onClick={() => bannerFile.current?.click()}
                  >
                    Replace Image
                  </Button>
                  <Button
                    internalType="outline"
                    disabled={uploading === 'banner'}
                    onClick={() =>
                      setConfirmation({
                        title: 'Remove the default banner?',
                        message: "Documents without a banner of their own won't show one.",
                        confirmLabel: 'Remove Banner',
                        onConfirm: () => saveFields({ defaultBanner: null })
                      })
                    }
                  >
                    Remove
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex aspect-[5/1] w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-divider">
                <span className="text-muted">No default banner</span>
                <Button
                  internalType="outline"
                  isLoading={uploading === 'banner'}
                  onClick={() => bannerFile.current?.click()}
                >
                  Upload Image
                </Button>
              </div>
            )}
            <input
              type="file"
              ref={bannerFile}
              className="hidden"
              accept="image/*"
              aria-label="Banner image"
              onChange={(event) => {
                const file = event.target.files?.[0]
                event.target.value = ''
                if (file) uploadBanner(file)
              }}
            />
          </div>
        </SettingsCard>

        <SettingsCard title="Document headings" description="How document names and section headings look.">
          <HeadingStyleRow
            name="Document name"
            description="How document names look."
            formatting={{
              fontSize: project.processTitleFontSize,
              colour: project.processTitleColour,
              bold: project.processTitleBold,
              italic: project.processTitleItalic,
              underline: project.processTitleUnderline,
              alignment: project.processTitleAlignment
            }}
            onUpdate={updateDocumentNameStyle}
            cantRemove="Documents always have a name"
          />
          {project.headingStyles.map((style: IHeadingStyle, index: number) => (
            <HeadingStyleRow
              key={style.publicId || index}
              name={`Heading ${index + 1}`}
              description={
                index === 0 ? 'How top-level section headings look.' : `How level ${index + 1} section headings look.`
              }
              formatting={style}
              onUpdate={updateHeadingStyle}
              // Only the deepest level can go: the levels below it would otherwise have nothing above them.
              onRemove={index === headingCount - 1 ? removeLastHeadingLevel : undefined}
              cantRemove="Only the deepest heading level can be removed"
              defaultOpen={!!style.publicId && style.publicId === addedHeading}
            />
          ))}
          <div className="mx-4 py-3 flex items-center gap-2">
            <Button internalType="outline" onClick={addHeadingLevel}>
              <Plus />
              Add Heading Level
            </Button>
          </div>
        </SettingsCard>

        <SettingsCard
          title="Word export format"
          description="Set the template used to export your documents to Word (.docx format)."
        >
          <div className="mx-4 py-3 flex flex-col gap-2 text-[13px]">
            {/* The template in use, as shadcn/ui's outline item: the file (its name downloads it), what
                it is, and its actions. */}
            <div className="flex items-center gap-4 rounded-lg border border-divider p-4">
              <div className="min-w-0 flex-1">
                <Tooltip content="Download">
                  <Button
                    internalType="transparent"
                    className="h-auto max-w-full justify-start gap-1.5 p-0 font-medium text-body hover:bg-transparent hover:text-body"
                    disabled={!!project.wordTemplate && !template}
                    onClick={() => window.open(project.wordTemplate ? template?.src : DEFAULT_TEMPLATE, '_blank')}
                  >
                    <span className="truncate pb-0.5 underline underline-offset-2">
                      {project.wordTemplate ? template?.name || 'Word template.docx' : 'Morta-Export-Template.docx'}
                    </span>
                    <Download className="size-3.5 text-muted" />
                  </Button>
                </Tooltip>
                <p className="mt-0.5 text-muted">
                  {project.wordTemplate
                    ? 'Your Word export template.'
                    : 'Example Word export template, used by default.'}
                </p>
              </div>
              <Button
                internalType="outline"
                data-tour="change-word-template"
                isLoading={uploading === 'template'}
                onClick={() => templateFile.current?.click()}
              >
                Replace Template
              </Button>
              {project.wordTemplate && (
                <Tooltip content="Remove template">
                  <span className="inline-flex">
                    <Button
                      internalType="outline"
                      aria-label="Remove template"
                      className="size-8 p-0"
                      disabled={uploading === 'template'}
                      onClick={() =>
                        setConfirmation({
                          title: 'Remove the Word template?',
                          message: "Exports will use Morta's default template.",
                          confirmLabel: 'Remove Template',
                          onConfirm: removeTemplate
                        })
                      }
                    >
                      <Delete />
                    </Button>
                  </span>
                </Tooltip>
              )}
              <input
                type="file"
                ref={templateFile}
                className="hidden"
                accept=".docx"
                aria-label="Word template"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ''
                  if (file) uploadTemplate(file)
                }}
              />
            </div>
            {missingStyles && <MissingStylesAlert styles={missingStyles} />}
            {/* What a template needs, each name to type exactly as a badge. The line height leaves the
                badges room when they wrap. */}
            <p className="leading-6 text-muted">
              A template needs the styles{' '}
              {TEMPLATE_STYLES.map((style, index) => (
                <React.Fragment key={style}>
                  <Code>{style}</Code>
                  {index < TEMPLATE_STYLES.length - 2 && ' '}
                  {index === TEMPLATE_STYLES.length - 2 && ' and '}
                </React.Fragment>
              ))}{' '}
              (or <Code>List Bullet1</Code>), and ideally <Code>Table Grid</Code> and a table of contents.{' '}
              <Code>{'{{document.name}}'}</Code> on its cover page becomes the document&apos;s name.{' '}
              {/* The starter template, always to hand, even once the hub has its own. */}
              <a className="text-muted underline! underline-offset-2 hover:text-body" href={DEFAULT_TEMPLATE}>
                Download our starter template
              </a>{' '}
              and customise it.
            </p>
          </div>

          {project.wordTemplate && project.wordTableStyles && project.wordTableStyles.length > 0 && (
            <FieldRow label="Table style" description="For tables in exported documents, from the template's styles.">
              <Select
                options={project.wordTableStyles.map((style) => ({ label: style, value: style }))}
                optionsSelected={project.wordTableStyle ? [project.wordTableStyle] : []}
                onOptionClick={(value) => saveFields({ wordTableStyle: value || null })}
                placeholder="Word's default"
                clearable
              />
            </FieldRow>
          )}
        </SettingsCard>

        <SettingsCard
          title="Table header themes"
          description="Set the design for new columns added to tables in this hub."
          footer={
            <>
              <p className="flex-1 text-muted">Use these header colours on every column already in the hub.</p>
              <Button internalType="outline" data-tour="bulk-apply-column-colours" onClick={applyHeaderColours}>
                Apply to All Columns
              </Button>
            </>
          }
        >
          {/* Above the header colours it shows, following them as they're picked. */}
          <div className="mx-4 flex justify-center pt-4 pb-3">
            <HeaderPreview
              background={headerBackgroundDraft ?? project.defaultHeaderBackgroundColor}
              text={headerTextDraft ?? project.defaultHeaderTextColor}
            />
          </div>
          <InlineRow
            label="Header background"
            note={
              project.defaultHeaderBackgroundColor.toLowerCase() !== DEFAULT_HEADER_BACKGROUND_COLOUR && (
                <TextLink
                  onClick={() => saveFields({ defaultHeaderBackgroundColor: DEFAULT_HEADER_BACKGROUND_COLOUR })}
                >
                  Reset to default
                </TextLink>
              )
            }
          >
            <ColourField
              label="Header background"
              value={project.defaultHeaderBackgroundColor}
              onDraft={setHeaderBackgroundDraft}
              onSave={(colour) => saveFields({ defaultHeaderBackgroundColor: colour })}
            />
          </InlineRow>

          <InlineRow
            label="Header text"
            note={
              project.defaultHeaderTextColor.toLowerCase() !== DEFAULT_HEADER_TEXT_COLOUR && (
                <TextLink onClick={() => saveFields({ defaultHeaderTextColor: DEFAULT_HEADER_TEXT_COLOUR })}>
                  Reset to default
                </TextLink>
              )
            }
          >
            <ColourField
              label="Header text"
              value={project.defaultHeaderTextColor}
              onDraft={setHeaderTextDraft}
              onSave={(colour) => saveFields({ defaultHeaderTextColor: colour })}
            />
          </InlineRow>
        </SettingsCard>

        <SettingsCard
          title="Table data formats"
          description="The formats a new date or date and time column starts with."
          footer={
            <>
              <p className="flex-1 text-muted">
                Use these formats on every date and date and time column already in the hub.
              </p>
              <Button internalType="outline" onClick={applyDataFormats}>
                Apply to All Columns
              </Button>
            </>
          }
        >
          <FieldRow
            label="Date format"
            note={
              !editDate.editing &&
              project.defaultDateFormat !== DEFAULT_DATE_FORMAT && (
                <TextLink onClick={() => saveDateFormat('date', DEFAULT_DATE_FORMAT)}>
                  Reset to {DEFAULT_DATE_FORMAT}
                </TextLink>
              )
            }
          >
            <div className="flex items-center gap-2" {...dateFocusProps}>
              <Input
                ref={dateInput}
                value={editDate.text}
                placeholder={DEFAULT_DATE_FORMAT}
                aria-label="Date format"
                onChange={(event) => setEditDate({ editing: true, text: event.target.value })}
              />
              {(dateFocused || editDate.editing) && (
                <SaveButtons
                  saving={savingField}
                  canSave={editDate.text.trim() !== '' && editDate.text !== project.defaultDateFormat}
                  onCancel={() => {
                    setEditDate({ editing: false, text: project.defaultDateFormat })
                    dateInput.current?.blur()
                    leaveDate()
                  }}
                  onSave={() => saveDateFormat('date', editDate.text.trim())}
                />
              )}
            </div>
          </FieldRow>

          <FieldRow
            label="Date and time format"
            note={
              !editDatetime.editing &&
              project.defaultDatetimeFormat !== DEFAULT_DATETIME_FORMAT && (
                <TextLink onClick={() => saveDateFormat('datetime', DEFAULT_DATETIME_FORMAT)}>
                  Reset to {DEFAULT_DATETIME_FORMAT}
                </TextLink>
              )
            }
          >
            <div className="flex items-center gap-2" {...datetimeFocusProps}>
              <Input
                ref={datetimeInput}
                value={editDatetime.text}
                placeholder={DEFAULT_DATETIME_FORMAT}
                aria-label="Date and time format"
                onChange={(event) => setEditDatetime({ editing: true, text: event.target.value })}
              />
              {(datetimeFocused || editDatetime.editing) && (
                <SaveButtons
                  saving={savingField}
                  canSave={editDatetime.text.trim() !== '' && editDatetime.text !== project.defaultDatetimeFormat}
                  onCancel={() => {
                    setEditDatetime({ editing: false, text: project.defaultDatetimeFormat })
                    datetimeInput.current?.blur()
                    leaveDatetime()
                  }}
                  onSave={() => saveDateFormat('datetime', editDatetime.text.trim())}
                />
              )}
            </div>
          </FieldRow>
        </SettingsCard>
      </div>

      <ConfirmDialog confirmation={confirmation} onClose={() => setConfirmation(null)} />
    </div>
  )
}

export default Formatting
